import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import ButtonContained from 'components/ButtonContained';

const PopoutDropdown = ({
    label,
    options,
    align = 'right',
    direction = 'down',
    buttonIcon = 'chevronDown',
    secondaryIcon,
    disabled,
    onOptionSelect,
    id,
    dataCy,
    fullWidth = false,
    largeButton = false
}) => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const [stylePos, setStylePos] = useState(null);

    // Close dropdown if label changes
    useEffect(() => {
        setOpen(false);
    }, [label]);

    const isButtonVisible = (el) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();

        // if completely outside viewport
        if (rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth) {
            return false;
        }

        let parent = el.parentElement;
        while (parent && parent !== document.body) {
            const style = window.getComputedStyle(parent);
            const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`;
            if (/auto|scroll|hidden/.test(overflow)) {
                const pr = parent.getBoundingClientRect();
                if (rect.right < pr.left || rect.left > pr.right || rect.bottom < pr.top || rect.top > pr.bottom) {
                    return false;
                }
            }
            parent = parent.parentElement;
        }

        return true;
    };

    // compute and set popup position relative to viewport (fixed) to avoid scroll-container clipping
    const computePosition = () => {
        const btn = buttonRef.current?.getBoundingClientRect();
        if (!btn) return;

        // hide if button is clipped / scrolled out of its scroll container or viewport
        if (!isButtonVisible(buttonRef.current)) {
            setOpen(false);
            return;
        }

        const dropdownWidth = dropdownRef.current?.offsetWidth || btn.width;
        const dropdownHeight = dropdownRef.current?.offsetHeight || 64;
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const spaceBelow = viewportHeight - btn.bottom;
        const spaceAbove = btn.top;
        const gap = 4; // space between button and popup
        const margin = 8; // margin from viewport edges
        let openDown = direction === 'up' ? false : true;

        // Determine if we should flip to open upward
        if (openDown && spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            openDown = false;
        }

        // Calculate vertical position and constrain max height
        let top;
        let maxHeight;
        
        if (openDown) {
            top = btn.bottom + gap;
            maxHeight = Math.min(dropdownHeight, spaceBelow - gap - margin);
        } else {
            const availableHeight = spaceAbove - gap - margin;
            maxHeight = Math.min(dropdownHeight, availableHeight);
            top = btn.top - maxHeight - gap;
        }

        // Calculate horizontal position and constrain within viewport
        let left = btn.left;

        if (align === 'right') {
            left = btn.right - dropdownWidth;
        }

        // Ensure dropdown doesn't overflow right edge
        if (left + dropdownWidth > viewportWidth - margin) {
            left = viewportWidth - dropdownWidth - margin;
        }

        // Ensure dropdown doesn't overflow left edge
        if (left < margin) {
            left = margin;
        }

        setStylePos({
            position: 'fixed',
            left: Math.round(left),
            top: Math.round(top),
            minWidth: btn.width,
            maxHeight: Math.round(maxHeight),
            zIndex: 9999
        });
    };

    useEffect(() => {
        if (!open) {
            setStylePos(null);
            return;
        }
        computePosition();

        const onResizeOrScroll = () => computePosition();

        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };

        window.addEventListener('resize', onResizeOrScroll);
        window.addEventListener('scroll', onResizeOrScroll, true);
        window.addEventListener('keydown', onKey);

        return () => {
            window.removeEventListener('resize', onResizeOrScroll);
            window.removeEventListener('scroll', onResizeOrScroll, true);
            window.removeEventListener('keydown', onKey);
        };
    }, [open]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)
            ) {
                setOpen(false);
            }
        };

        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open]);

    return (
        <div className={`relative ${fullWidth ? 'w-full' : 'w-auto'}`}>
            <div ref={buttonRef} onClick={(e) => e.stopPropagation()}>
                <ButtonContained
                    id={id}
                    dataCy={dataCy}
                    text={label}
                    disabled={disabled}
                    icon={buttonIcon}
                    secondaryIcon={secondaryIcon}
                    type={largeButton ? 'defaultOutline' : 'mediumOutlineIconRight'}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!disabled) setOpen((o) => !o);
                    }}
                    className={fullWidth ? 'w-full' : ''}
                />
            </div>

            {open &&
                ReactDOM.createPortal(
                    <div
                        ref={dropdownRef}
                        style={{
                            visibility: stylePos?.left && stylePos?.top ? 'visible' : 'hidden',
                            position: stylePos?.position ?? 'fixed',
                            left: stylePos?.left ?? 0,
                            top: stylePos?.top ?? 0,
                            maxHeight: stylePos?.maxHeight ? `${stylePos.maxHeight}px` : undefined,
                            minWidth: stylePos?.minWidth ? `${stylePos.minWidth}px` : undefined,
                            zIndex: stylePos?.zIndex ?? 9999
                        }}
                        className={`bg-[var(--color-white)] shadow-md rounded-md whitespace-nowrap p-5 overflow-y-auto custom-scrollbar`}>
                        {options.map((option, index) => (
                            <button
                                key={index}
                                id={`Popout-dropdown-option-${index + 1}-${option.name}`}
                                data-cy={`Popout-dropdown-option-${index + 1}-${option.name}`}
                                disabled={option.disabled}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onOptionSelect) {
                                        onOptionSelect(option.value, option.name);
                                        setOpen(false);
                                    }
                                }}
                                className={`tw-paragraph-reg block w-full text-left px-4 py-2 hover:bg-[var(--color-gray-100)] rounded-[8px] ${
                                    option.disabled ? 'opacity-50 cursor-not-allowed' : ''
                                }`}>
                                {option.name}
                            </button>
                        ))}
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default PopoutDropdown;
